import { gcm } from '@noble/ciphers/aes';
import { bytesToHex, bytesToUtf8, hexToBytes, utf8ToBytes } from '@noble/ciphers/utils';
import { randomBytes } from '@noble/ciphers/webcrypto';
import { secp256r1 } from '@noble/curves/p256';
import { sha256 } from '@noble/hashes/sha256';
import { ECPrivateKey } from '@peculiar/asn1-ecc';
import { AsnParser } from '@peculiar/asn1-schema';
import { SubjectPublicKeyInfo } from '@peculiar/asn1-x509';
export function sign(privateKeyPem, data) {
    const privateKey = extractPrivateKeyFromPEM(privateKeyPem);
    const msgHash = sha256(utf8ToBytes(data));
    return secp256r1.sign(msgHash, privateKey).toCompactHex();
}
export function verifySignature(publicKeyPem, data, signature) {
    const publicKey = extracPublicKeyFromPEM(publicKeyPem);
    const signatureBytes = hexToBytes(signature);
    const msgHash = sha256(utf8ToBytes(data));
    return secp256r1.verify(signatureBytes, msgHash, publicKey);
}
function extractPrivateKeyFromPEM(pemKey) {
    const base64Key = pemKey
        .replace(/-----BEGIN EC PRIVATE KEY-----/, '')
        .replace(/-----END EC PRIVATE KEY-----/, '')
        .replace(/\n/g, '')
        .trim();
    const der = Uint8Array.from(atob(base64Key), (c) => c.charCodeAt(0));
    // Parse ASN.1 DER to extract the private key
    const ecPrivateKey = AsnParser.parse(der, ECPrivateKey);
    return new Uint8Array(ecPrivateKey.privateKey.buffer);
}
function extracPublicKeyFromPEM(pemKey) {
    const base64Key = pemKey
        .replace(/-----BEGIN PUBLIC KEY-----/, '')
        .replace(/-----END PUBLIC KEY-----/, '')
        .replace(/\n/g, '')
        .trim();
    const der = Uint8Array.from(atob(base64Key), (c) => c.charCodeAt(0));
    // Parse ASN.1 DER to extract the public key
    const spki = AsnParser.parse(der, SubjectPublicKeyInfo);
    return new Uint8Array(spki.subjectPublicKey);
}
/**
 * Encrypts a buffer
 * @param key encryption key
 * @param cleartext buffer
 */
export function encrypt(key, cleartext) {
    const iv = randomBytes(24);
    const aes = gcm(key, iv);
    const ciphertext = aes.encrypt(cleartext);
    return { iv, ciphertext };
}
/**
 * Decrypts a buffer
 * @param key encryption key
 * @param iv initialization vector used when encrypted
 * @param ciphertext encrypted buffer
 */
export function decrypt(key, iv, ciphertext) {
    const aes = gcm(key, iv);
    return aes.decrypt(ciphertext);
}
/**
 * Convenience class for dealing with aes-256-gcm encrypted buffers
 */
export class EncryptedValue {
    iv;
    ciphertext;
    constructor({ iv, ciphertext }) {
        this.iv = iv;
        this.ciphertext = ciphertext;
    }
    /**
     * Creates a new EncryptedValue by encrypting data
     * @param key encryption key
     * @param cleartext data to encrypt
     */
    static encrypt(key, cleartext) {
        const encryptedValue = encrypt(hexToBytes(key), utf8ToBytes(cleartext));
        return new EncryptedValue({
            iv: encryptedValue.iv,
            ciphertext: encryptedValue.ciphertext,
        });
    }
    /**
     * Decodes an EncryptedValue from base64
     */
    static decode({ iv, ciphertext, }) {
        return new EncryptedValue({
            iv: hexToBytes(iv),
            ciphertext: hexToBytes(ciphertext),
        });
    }
    /**
     * Decrypts this EncryptedValue
     * @param key encryption key
     */
    decrypt(key) {
        const keyBytes = hexToBytes(key);
        const decrypted = decrypt(keyBytes, this.iv, this.ciphertext);
        return bytesToUtf8(decrypted);
    }
    /**
     * Overridden JSON formatting (convert buffers to base64)
     */
    toJSON() {
        return {
            iv: bytesToHex(this.iv),
            ciphertext: bytesToHex(this.ciphertext),
        };
    }
}
export function generateKeyPair() {
    const privateKey = bytesToHex(secp256r1.utils.randomPrivateKey());
    const publicKey = bytesToHex(secp256r1.getPublicKey(privateKey));
    return { privateKey, publicKey };
}
export function computeSharedSecret(privateKeyHex, publicKeyHex) {
    const privateKey = hexToBytes(privateKeyHex);
    const publicKey = hexToBytes(publicKeyHex);
    const sharedSecret = secp256r1.getSharedSecret(privateKey, publicKey).slice(1);
    return bytesToHex(sharedSecret);
}
//# sourceMappingURL=encryption.js.map